import React, { FC, useMemo } from "react";
import * as ToggleGroupRadix from "@radix-ui/react-toggle-group";
import { DeprecatedIconType } from "../deprecatedIcons";
import Text from "@ui/text";
import DeprecatedIcon from "./DeprecatedIcon";
import AutoLayout from "./AutoLayout";
import { StyleSheet } from "aphrodite";
import { deprecatedTones } from "@src/deprecatedDesignSystem/styles/deprecatedColors";
import { HOVER_TRANSITION } from "../styles/transitions";
import { shadows } from "../styles/shadows";
import useHover from "@src/hooks/useHover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";

export interface Option<T extends string> {
  value: T;
  icon?: DeprecatedIconType;
  label?: string;
  iconSize?: number;
  disabled?: boolean;
}
type ToggleAlignment = "horizontal" | "vertical";
type ToggleVariant = "white-over-gray" | "gray-over-white" | "icon";
type Props<T extends string> = {
  defaultValue: string;
  options: Option<T>[];
  alignment?: ToggleAlignment;
  variant?: ToggleVariant;
  value: T;
  onValueChange: (newValue: T) => void;
};

const ToggleGroup = <T extends string>({
  alignment = "horizontal",
  variant = "gray-over-white",
  ...props
}: Props<T>): JSX.Element => {
  return (
    <ToggleGroupRadix.Root
      type="single"
      rovingFocus={false}
      value={props.value}
      defaultValue="center"
      aria-label="Text alignment"
      onValueChange={(value) => {
        if (value !== "") {
          props.onValueChange(value as T);
        }
      }}
      asChild
    >
      <AutoLayout
        flex={1}
        padding={4}
        spaceBetweenItems={2}
        styleDeclaration={[
          variant === "gray-over-white" && styles.grayOverWhiteGroup,
          variant === "white-over-gray" && styles.whiteOverGrayGroup,
          variant === "icon" && styles.iconGroup,
          alignment === "horizontal"
            ? styles.horizontalGroup
            : styles.verticalGroup,
        ]}
      >
        {props.options.map((option) => {
          return (
            <ToggleItem
              key={option.value}
              alignment={alignment}
              variant={variant}
              value={props.value}
              option={option}
            />
          );
        })}
      </AutoLayout>
    </ToggleGroupRadix.Root>
  );
};

type ToggleItemProps<T extends string> = {
  value: T;
  option: Option<T>;
  alignment: ToggleAlignment;
  variant: ToggleVariant;
};
const ToggleItem = <T extends string>(
  props: ToggleItemProps<T>,
): JSX.Element => {
  const [hover, hoverBind] = useHover();
  const iconTextHoverColor =
    props.variant !== "icon"
      ? deprecatedTones.black
      : props.value === props.option.value
        ? deprecatedTones.black
        : deprecatedTones.white;
  const iconTextColor =
    props.value === props.option.value
      ? deprecatedTones.black
      : props.variant === "icon"
        ? deprecatedTones.gray3
        : deprecatedTones.gray6;
  return (
    <ToggleGroupRadix.Item
      asChild
      key={props.option.value}
      value={props.option.value}
      aria-label={props.option.label}
      disabled={props.option.disabled}
      {...hoverBind}
    >
      <AutoLayout
        flex={1}
        alignmentVertical="center"
        alignmentHorizontal="center"
        direction={props.alignment}
        styleDeclaration={[
          styles.item,
          props.option.disabled && styles.cursorNotAllowed,
          !props.option.disabled && styles.cursorPointer,
          props.option.value === props.value &&
            props.variant === "gray-over-white" &&
            styles.grayOverWhiteSelectedItem,
          props.value === props.value &&
            props.variant === "white-over-gray" &&
            styles.whiteOverGraySelectedItem,
          props.value === props.value &&
            props.variant === "icon" &&
            styles.iconSelectedItem,
          props.option.value !== props.value &&
            props.variant === "gray-over-white" &&
            styles.grayOverWhiteNotSelectedItem,
          props.option.value !== props.value &&
            props.variant === "white-over-gray" &&
            styles.whiteOverGrayNotSelectedItem,
          props.option.value !== props.value &&
            props.variant === "icon" &&
            styles.iconNotSelectedItem,
          props.alignment === "horizontal" &&
            props.option.icon &&
            styles.horizontalItem,
          props.alignment === "horizontal" &&
            !props.option.icon &&
            styles.noIconItem,
          props.alignment === "vertical" && styles.verticalItem,
          props.variant === "icon" && styles.iconItem,
        ]}
        spaceBetweenItems={props.alignment === "horizontal" ? 2 : 0}
      >
        {props.option.icon && (
          <TooltipIcon
            icon={props.option.icon}
            iconSize={props.option.iconSize}
            color={
              hover && !props.option.disabled
                ? iconTextHoverColor
                : iconTextColor
            }
            tooltipLabel={
              props.variant === "icon" ? props.option.label : undefined
            }
          />
        )}
        {props.variant !== "icon" && (
          <Text
            type="P2"
            fontWeight="Medium"
            color={
              hover && !props.option.disabled
                ? iconTextHoverColor
                : iconTextColor
            }
          >
            {props.option.label}
          </Text>
        )}
      </AutoLayout>
    </ToggleGroupRadix.Item>
  );
};

type TooltipIconProps = {
  color: string;
  icon: DeprecatedIconType;
  iconSize?: number;
  tooltipLabel?: string;
};

const TooltipIcon: FC<TooltipIconProps> = (props) => {
  const IconComponent = useMemo(() => {
    return (
      <AutoLayout>
        <DeprecatedIcon
          type={props.icon}
          color={props.color}
          iconSize={props.iconSize ? props.iconSize : 20}
        />
      </AutoLayout>
    );
  }, [props.color, props.icon, props.iconSize]);

  if (!props.tooltipLabel) {
    return IconComponent;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{IconComponent}</TooltipTrigger>
      <TooltipContent side="top" sideOffset={12}>
        {props.tooltipLabel}
      </TooltipContent>
    </Tooltip>
  );
};

const styles = StyleSheet.create({
  item: {
    border: "1px solid transparent",
    ":hover": {
      transition: HOVER_TRANSITION,
    },
  },
  cursorPointer: {
    cursor: "pointer",
  },
  cursorNotAllowed: {
    cursor: "not-allowed",
  },
  iconGroup: {
    backgroundColor: deprecatedTones.gray8Alpha,
  },
  grayOverWhiteGroup: {
    backgroundColor: deprecatedTones.white,
    border: `1px solid ${deprecatedTones.gray2}`,
  },
  grayOverWhiteNotSelectedItem: {
    backgroundColor: deprecatedTones.white,
  },
  grayOverWhiteSelectedItem: {
    backgroundColor: deprecatedTones.gray4Alpha,
  },
  whiteOverGrayGroup: {
    backgroundColor: deprecatedTones.gray4Alpha,
    border: `1px solid transparent`,
  },
  whiteOverGrayNotSelectedItem: {
    backgroundColor: deprecatedTones.gray4,
    border: "1px solid transparent",
    boxShadow: "none",
  },
  whiteOverGraySelectedItem: {
    border: `1px solid ${deprecatedTones.gray4Alpha}`,
    backgroundColor: deprecatedTones.white,
    boxShadow: shadows.elevation6,
  },
  iconSelectedItem: {
    backgroundColor: deprecatedTones.white,
  },
  iconNotSelectedItem: {
    backgroundColor: "transparent",
    ":hover": {
      backgroundColor: deprecatedTones.gray3Alpha,
    },
  },
  horizontalGroup: {
    borderRadius: 1000,
  },
  verticalGroup: {
    borderRadius: 10,
  },
  noIconItem: {
    borderRadius: 1000,
    paddingTop: 2,
    paddingLeft: 6,
    paddingRight: 6,
    paddingBottom: 2,
  },
  horizontalItem: {
    borderRadius: 1000,
    paddingTop: 2,
    paddingLeft: 6,
    paddingRight: 14,
    paddingBottom: 2,
  },
  verticalItem: {
    borderRadius: 6,
    paddingTop: 2,
    paddingBottom: 4,
  },
  iconItem: {
    display: "flex",
    padding: 0,
    width: 32,
    height: 32,
  },
});

export default ToggleGroup;
